import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVideoStream } from "../../providers/videoStreams";
import { useUser } from "../../providers/users";
import {
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { FiEdit, FiEye, FiTrash2, FiCopy } from "react-icons/fi";
import { DetailContainer } from "./styles";
import ModalConfirmDeletion from "../ModalConfirmDeletion";

const VideoStreamDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleAdmin } = useUser();
  const {
    currentVideoStream,
    getVideoStreamById,
    updateVideoStream,
    deleteVideoStream,
    getPlaybackUrl,
  } = useVideoStream();

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [embedUrl, setEmbedUrl] = useState("");

  useEffect(() => {
    const fetchVideo = async () => {
      if (id) {
        setIsLoading(true);
        const video = await getVideoStreamById(id);

        if (video) {
          setName(video.name);
          setDescription(video.description || "");
          setIsActive(video.isActive);

          // If video is ready, get playback URL
          if (video.status === "ready") {
            const playbackData = await getPlaybackUrl(id);
            if (playbackData) {
              setEmbedUrl(playbackData.embedUrl);
            }
          }
        }

        setIsLoading(false);
      }
    };

    fetchVideo();
  }, [id]);

  const handleSave = async () => {
    if (!id) return;

    await updateVideoStream(id, {
      name,
      description,
      isActive,
    });

    setIsEditing(false);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleView = async () => {
    if (!id) return;

    const playbackData = await getPlaybackUrl(id);
    if (playbackData) {
      window.open(playbackData.embedUrl, "_blank");
    }
  };

  const copyEmbedCode = () => {
    if (embedUrl) {
      const embedCode = `<iframe src="${embedUrl}" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowfullscreen="true"></iframe>`;
      navigator.clipboard.writeText(embedCode);
      alert(t("embed_code_copied"));
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "ready":
        return t("status_ready");
      case "processing":
        return t("status_processing");
      case "error":
        return t("status_error");
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ready":
        return "success";
      case "processing":
        return "warning";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  if (isLoading) {
    return (
      <DetailContainer>
        <div className="loadingContainer">
          <CircularProgress />
        </div>
      </DetailContainer>
    );
  }

  if (!currentVideoStream) {
    return (
      <DetailContainer>
        <Paper className="errorContainer">
          <Typography variant="h6">{t("video_not_found")}</Typography>
          <Button variant="contained" onClick={() => navigate(-1)}>
            {t("go_back")}
          </Button>
        </Paper>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <div className="header">
        <div>
          <Typography variant="h5">
            {isEditing ? t("edit_video") : currentVideoStream.name}
          </Typography>
          <Chip
            label={getStatusLabel(currentVideoStream.status)}
            color={getStatusColor(currentVideoStream.status) as any}
            className="statusChip"
          />
        </div>

        <div className="actionButtons">
          {roleAdmin && (
            <>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditing(false)}
                  >
                    {t("cancel")}
                  </Button>
                  <Button variant="contained" onClick={handleSave}>
                    {t("save")}
                  </Button>
                </>
              ) : (
                <>
                  {currentVideoStream.status === "ready" && (
                    <Button
                      variant="outlined"
                      startIcon={<FiEye />}
                      onClick={handleView}
                    >
                      {t("view")}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<FiEdit />}
                    onClick={() => setIsEditing(true)}
                  >
                    {t("edit")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<FiTrash2 />}
                    onClick={handleDelete}
                  >
                    {t("delete")}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <Paper className="videoDetails">
        {isEditing ? (
          <div className="editForm">
            <TextField
              label={t("video_name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              label={t("video_description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={4}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t("video_active")}
            />
          </div>
        ) : (
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              md={currentVideoStream.status === "ready" ? 6 : 12}
            >
              <div className="infoSection">
                <Typography variant="h6">{t("video_information")}</Typography>

                <div className="infoRow">
                  <Typography variant="subtitle2">{t("video_id")}</Typography>
                  <Typography>{currentVideoStream.id}</Typography>
                </div>

                <div className="infoRow">
                  <Typography variant="subtitle2">
                    {t("cloudflare_id")}
                  </Typography>
                  <Typography>
                    {currentVideoStream.cloudflareVideoId}
                  </Typography>
                </div>

                <div className="infoRow">
                  <Typography variant="subtitle2">{t("status")}</Typography>
                  <Chip
                    label={getStatusLabel(currentVideoStream.status)}
                    color={getStatusColor(currentVideoStream.status) as any}
                    size="small"
                  />
                </div>

                {currentVideoStream.duration && (
                  <div className="infoRow">
                    <Typography variant="subtitle2">{t("duration")}</Typography>
                    <Typography>
                      {Math.floor(currentVideoStream.duration / 60)}:
                      {(currentVideoStream.duration % 60)
                        .toString()
                        .padStart(2, "0")}
                    </Typography>
                  </div>
                )}

                <div className="infoRow">
                  <Typography variant="subtitle2">
                    {t("description")}
                  </Typography>
                  <Typography>
                    {currentVideoStream.description || t("no_description")}
                  </Typography>
                </div>

                <div className="infoRow">
                  <Typography variant="subtitle2">{t("created_at")}</Typography>
                  <Typography>
                    {new Date(currentVideoStream.createdAt).toLocaleString()}
                  </Typography>
                </div>

                <div className="infoRow">
                  <Typography variant="subtitle2">{t("views")}</Typography>
                  <Typography>{currentVideoStream.viewCount}</Typography>
                </div>

                <div className="infoRow">
                  <Typography variant="subtitle2">
                    {t("minutes_viewed")}
                  </Typography>
                  <Typography>
                    {currentVideoStream.minutesViewed.toFixed(1)}
                  </Typography>
                </div>
              </div>
            </Grid>

            {currentVideoStream.status === "ready" && (
              <Grid item xs={12} md={6}>
                <div className="previewSection">
                  <Typography variant="h6">{t("preview")}</Typography>

                  {embedUrl ? (
                    <>
                      <div className="videoPreview">
                        <iframe
                          src={embedUrl}
                          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                          allowFullScreen
                          title={currentVideoStream.name}
                        />
                      </div>

                      <Button
                        variant="outlined"
                        startIcon={<FiCopy />}
                        onClick={copyEmbedCode}
                        className="embedButton"
                      >
                        {t("copy_embed_code")}
                      </Button>
                    </>
                  ) : (
                    <div className="loadingPreview">
                      <CircularProgress size={24} />
                      <Typography>{t("loading_preview")}</Typography>
                    </div>
                  )}
                </div>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {isDeleteModalOpen && (
        <ModalConfirmDeletion
          setModalConfirmDeletion={setIsDeleteModalOpen}
          deleteFunction={deleteVideoStream}
          deleteId={id!}
        />
      )}
    </DetailContainer>
  );
};

export default VideoStreamDetails;
